import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Entry, RateCacheEntry, Settings } from '@/types/domain'

export const STORAGE_NAMESPACE = 'folha-fiscal:'

export const STORAGE_KEYS = {
  entries: `${STORAGE_NAMESPACE}entries`,
  rateCache: `${STORAGE_NAMESPACE}rateCache`,
  settings: `${STORAGE_NAMESPACE}settings`,
  schemaVersion: `${STORAGE_NAMESPACE}schemaVersion`,
} as const

// ---------------------------------------------------------------------------
// Entries slice — folha-fiscal:entries
// ---------------------------------------------------------------------------

export interface EntriesState {
  entries: Entry[]
  add: (entry: Entry) => void
  update: (id: string, patch: Partial<Entry>) => void
  remove: (id: string) => void
  replace: (entries: Entry[]) => void
  clear: () => void
}

export const useEntriesStore = create<EntriesState>()(
  persist(
    (set) => ({
      entries: [],
      add: (entry) =>
        set((s) => ({ entries: [...s.entries, entry] })),
      update: (id, patch) =>
        set((s) => ({
          entries: s.entries.map((e) =>
            e.id === id ? { ...e, ...patch, atualizadoEm: new Date().toISOString() } : e,
          ),
        })),
      remove: (id) =>
        set((s) => ({ entries: s.entries.filter((e) => e.id !== id) })),
      replace: (entries) => set({ entries }),
      clear: () => set({ entries: [] }),
    }),
    {
      name: STORAGE_KEYS.entries,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (s) => ({ entries: s.entries }),
    },
  ),
)

// ---------------------------------------------------------------------------
// Rate cache slice — folha-fiscal:rateCache
// ---------------------------------------------------------------------------

function rateCacheKey(source: string, series: string, date: string): string {
  return `${source}:${series}:${date}`
}

export interface RateCacheState {
  rates: RateCacheEntry[]
  upsert: (entry: RateCacheEntry) => void
  bulkUpsert: (entries: RateCacheEntry[]) => void
  clear: () => void
}

export const useRateCacheStore = create<RateCacheState>()(
  persist(
    (set) => ({
      rates: [],
      upsert: (entry) =>
        set((s) => {
          const targetKey = rateCacheKey(entry.source, entry.series, entry.date)
          const idx = s.rates.findIndex(
            (r) => rateCacheKey(r.source, r.series, r.date) === targetKey,
          )
          if (idx === -1) return { rates: [...s.rates, entry] }
          const next = s.rates.slice()
          next[idx] = entry
          return { rates: next }
        }),
      bulkUpsert: (entries) =>
        set((s) => {
          const map = new Map(
            s.rates.map((r) => [rateCacheKey(r.source, r.series, r.date), r]),
          )
          for (const e of entries) {
            map.set(rateCacheKey(e.source, e.series, e.date), e)
          }
          return { rates: Array.from(map.values()) }
        }),
      clear: () => set({ rates: [] }),
    }),
    {
      name: STORAGE_KEYS.rateCache,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (s) => ({ rates: s.rates }),
    },
  ),
)

// ---------------------------------------------------------------------------
// Settings slice — folha-fiscal:settings
// ---------------------------------------------------------------------------

export const DEFAULT_SETTINGS: Settings = {
  paranoidMode: { enabled: false, ttlDays: 30 },
  lastAccessedAt: new Date(0).toISOString(),
  knownCategorias: [],
  knownTags: [],
  knownPagadores: [],
  privacyBannerDismissed: false,
  defaultCurrency: 'USD',
  schemaVersion: 1,
}

export interface SettingsState {
  settings: Settings
  patch: (patch: Partial<Settings>) => void
  touchLastAccessed: () => void
  reset: () => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      settings: DEFAULT_SETTINGS,
      patch: (patch) =>
        set((s) => ({ settings: { ...s.settings, ...patch } })),
      touchLastAccessed: () =>
        set((s) => ({
          settings: { ...s.settings, lastAccessedAt: new Date().toISOString() },
        })),
      reset: () => set({ settings: DEFAULT_SETTINGS }),
    }),
    {
      name: STORAGE_KEYS.settings,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (s) => ({ settings: s.settings }),
    },
  ),
)
